import React, { useState, useEffect, useRef } from 'react';

const API_URL = "https://l0u763aj2k.execute-api.us-east-1.amazonaws.com/prod/generate";

const MODEL_OPTIONS = [
  { label: 'Amazon Titan', value: 'amazon.titan-image-generator-v1', maxSize: 1408, icon: '⚡' },
  { label: 'Stable Diffusion XL', value: 'stability.stable-diffusion-xl-v1', maxSize: 1024, icon: '🎨' }
];

// Size presets optimized for each model
const SIZE_OPTIONS = [
  { label: 'Small Square (512x512)', value: { width: 512, height: 512 } },
  { label: 'Medium Square (768x768)', value: { width: 768, height: 768 } },
  { label: 'Large Square (1024x1024)', value: { width: 1024, height: 1024 } },
  { label: 'Custom', value: 'custom' }
];

// Removed ARYAA_API_URL - AI suggest functionality temporarily disabled

function PromptForm() {
  const [model, setModel] = useState(MODEL_OPTIONS[0].value);
  const [size, setSize] = useState(SIZE_OPTIONS[0].value);
  const [customSize, setCustomSize] = useState({ width: 512, height: 512 });
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [chatHistory, setChatHistory] = useState([]); // [{prompt, result, timestamp}]
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  // Add a ref to the form so we can submit it programmatically
  const formRef = useRef();
  const [referenceImage, setReferenceImage] = useState(null);
  const [copySuccess, setCopySuccess] = useState(null);
  // Removed Aryaa state variables - AI suggest functionality temporarily disabled

  // Load chat history from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('lightninggen_chat_history');
    if (stored) {
      try {
        setChatHistory(JSON.parse(stored));
      } catch {}
    }
  }, []);

  // Save chat history to localStorage on update
  useEffect(() => {
    localStorage.setItem('lightninggen_chat_history', JSON.stringify(chatHistory));
  }, [chatHistory]);

  // Clear chat history handler
  const handleClearHistory = () => {
    setChatHistory([]);
    localStorage.removeItem('lightninggen_chat_history');
  };

  // Setup SpeechRecognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setPrompt(prev => prev ? prev + ' ' + transcript : transcript);
        setListening(false);
      };
      recognitionRef.current.onend = () => setListening(false);
      recognitionRef.current.onerror = () => setListening(false);
    }
  }, []);

  const handleMicClick = (e) => {
    e.preventDefault();
    if (!recognitionRef.current) return;
    setListening(true);
    recognitionRef.current.start();
  };

  // Add a helper to set prompt and submit
  const handlePromptSuggestion = (suggestion) => {
    setPrompt(suggestion);
    setTimeout(() => {
      if (formRef.current) {
        formRef.current.requestSubmit();
      }
    }, 0);
  };

  // Get current model's max size
  const currentModel = MODEL_OPTIONS.find(m => m.value === model);
  const maxSize = currentModel ? currentModel.maxSize : 1408;

  const handleSizeChange = (e) => {
    const val = JSON.parse(e.target.value);
    setSize(val);
  };

  const handleModelChange = (e) => {
    const newModel = e.target.value;
    setModel(newModel);
    
    // Update custom size if it exceeds the new model's limits
    const newModelInfo = MODEL_OPTIONS.find(m => m.value === newModel);
    if (newModelInfo) {
      let newCustomSize = { ...customSize };
      
      // Ensure size doesn't exceed new model's limits
      if (customSize.width > newModelInfo.maxSize) {
        newCustomSize.width = newModelInfo.maxSize;
      }
      if (customSize.height > newModelInfo.maxSize) {
        newCustomSize.height = newModelInfo.maxSize;
      }
      
      // For Titan model, ensure square resolution
      if (newModel === 'amazon.titan-image-generator-v1') {
        const maxDimension = Math.min(newCustomSize.width, newCustomSize.height, newModelInfo.maxSize);
        newCustomSize = { width: maxDimension, height: maxDimension };
      }
      
      setCustomSize(newCustomSize);
    }
  };

  const validateCustomSize = () => {
    if (size === 'custom') {
      if (customSize.width > maxSize || customSize.height > maxSize) {
        return `Maximum size for ${currentModel.label} is ${maxSize}x${maxSize} pixels`;
      }
      if (customSize.width < 64 || customSize.height < 64) {
        return 'Minimum size is 64x64 pixels';
      }
      
      // Titan model only supports square resolutions
      if (model === 'amazon.titan-image-generator-v1' && customSize.width !== customSize.height) {
        return 'Titan model only supports square resolutions (width must equal height)';
      }
      
      // Additional validation for SDXL: total pixels must not exceed 1,048,576
      if (model === 'stability.stable-diffusion-xl-v1') {
        const totalPixels = customSize.width * customSize.height;
        if (totalPixels > 1048576) {
          return `Total pixels (${totalPixels}) exceeds SDXL limit of 1,048,576 pixels`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateCustomSize();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    
    // Context chaining: prepend last prompt if exists
    let contextualPrompt = prompt;
    if (chatHistory.length > 0) {
      contextualPrompt = chatHistory[chatHistory.length - 1].prompt + ". " + prompt;
    }

    const requestBody = {
      model,
      size: size === 'custom' ? customSize : size,
      prompt: contextualPrompt
    };
    
    console.log('Sending request:', requestBody);
    
    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody)
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Success response:', data);
      setResult(data);
      // Add to chat history
      setChatHistory([...chatHistory, {
        prompt,
        result: data,
        timestamp: Date.now()
      }]);
    } catch (err) {
      console.error('Fetch error:', err);
      // Try to extract error message from API error response
      let errorMsg = "An error occurred. Please try again.";
      if (err.response?.data?.error?.toLowerCase().includes("guardrail")) {
        errorMsg = "Your prompt was blocked by our safety guardrails. Please revise and try again.";
      } else if (err.message && err.message.includes("blocked by our content filters")) {
        errorMsg = "Your prompt was blocked by our safety guardrails. Please revise and try again.";
      } else if (err.message) {
        // Try to parse error message from API error response
        try {
          const apiError = JSON.parse(err.message.split(':').slice(1).join(':'));
          if (apiError.error && apiError.error.includes("blocked by our content filters")) {
            errorMsg = "Your prompt was blocked by our safety guardrails. Please revise and try again.";
          }
        } catch {}
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // On mount, check for figmaImage param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const figmaImage = params.get('figmaImage');
    if (figmaImage) {
      setReferenceImage(figmaImage);
    }
  }, []);

  // Removed handleAryaaSuggest function - AI suggest functionality temporarily disabled

  return (
    <div className="form-container">
      {/* Modern Header */}
      <header className="header">
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-r from-cyan-600 to-cyan-700 rounded-xl shadow-lg">
              <span className="text-2xl">⚡</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold gradient-text">
                LightningGen
              </h1>
              <p className="text-sm text-slate-600 font-medium">Professional AI Image Generation</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="badge badge-primary">
              ⚡ Enterprise Ready
            </span>
            <span className="badge badge-secondary">
              🔒 Secure & Compliant
            </span>
          </div>
          {/* Feature Badges Row styled like main badges */}
          <div className="flex flex-wrap gap-2 mt-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '0.5rem' }}>
            <span className="badge badge-primary">🛡️ Guardrails</span>
            <span className="badge badge-primary">🖼️ High-Quality Images</span>
            <span className="badge badge-primary">💬 Chat History</span>
            <span className="badge badge-primary">🎤 Voice-to-Text</span>
            <span className="badge badge-secondary">🔥 Trending Images</span>
            <span className="badge badge-secondary">⚡ 1-Click Prompts</span>
            <span className="badge badge-secondary">📝 Alt-text</span>
            <span className="badge badge-secondary">🛑 Error Handling</span>
            <span className="badge badge-secondary">🎨 UI/UX Design</span>
            <span className="badge badge-secondary">⏳ Loading States</span>
            <span className="badge badge-secondary">🖼️ Gallery</span>
            <span className="badge badge-secondary">📐 Custom Sizes</span>
            <span className="badge badge-secondary">⏱️ Latency View</span>
          </div>
        </div>
      </header>

      {/* Main Form Grid */}
      <div className="form-grid">
        {/* Input Panel */}
        <div className="form-section">
          {/* Moved Hero Section Text */}
          <div className="mb-8">
            <h1 className="gradient-text text-2xl font-bold mb-2">Transform Your Brand<br /><span className="text-3xl">with AI-Powered Imagery</span></h1>
            <p className="text-slate-600">
              Create stunning, professional-grade visuals using cutting-edge Stable Diffusion SDXL and Amazon Titan AI models. Perfect for marketing, branding, and content creation.
            </p>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-3">
            <span className="text-2xl">🏢</span>
            Professional Image Creation
          </h2>
          {/* Features Completed Section */}
          {/* Remove the Features Completed section (the green box with the bulleted list) */}

          <p className="text-slate-600 mb-8">
            Configure your brand assets and generate high-quality imagery for professional use
          </p>

          {/* Contextual Chat History Display */}
          {chatHistory.length > 0 && (
            <div className="chat-history mb-8 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-slate-700">Contextual Chat History</h3>
                <button onClick={handleClearHistory} className="btn btn-xs btn-danger ml-4">Clear History</button>
              </div>
              <div className="space-y-4">
                {chatHistory.map((entry, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-2 bg-white rounded shadow-sm">
                    <div className="flex-1">
                      <div className="font-semibold text-slate-800 cursor-pointer hover:underline" onClick={() => setPrompt(entry.prompt)}>
                        {entry.prompt}
                      </div>
                      {entry.result?.imageUrl && (
                        <div className="flex items-center gap-2 mt-1">
                          <img src={entry.result.imageUrl} alt={entry.prompt} style={{maxWidth: 120, maxHeight: 80, borderRadius: 4}} />
                          <a href={entry.result.imageUrl} download={`lightninggen-image-${idx+1}.png`} className="btn btn-xs btn-primary" title="Download image">
                            ⬇️
                          </a>
                        </div>
                      )}
                      <div className="text-xs text-slate-400 mt-1">{new Date(entry.timestamp).toLocaleTimeString()}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            {/* Show reference image from Figma if present */}
            {referenceImage && (
              <div className="mb-4">
                <div className="text-xs text-slate-500 mb-1">Reference image from Figma:</div>
                <img src={`data:image/png;base64,${referenceImage}`} alt="Figma Reference" className="rounded shadow max-w-full max-h-40 border" />
              </div>
            )}
            {/* Model Selection */}
            <div className="form-group">
              <label className="label">AI Model Selection</label>
              <div className="select">
                <select value={model} onChange={handleModelChange} className="input">
                  {MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.icon} {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {currentModel?.label === 'Amazon Titan' 
                  ? 'Enterprise-grade, scalable generation with square resolution support'
                  : 'Premium quality, detailed imagery with flexible dimensions'
                }
              </p>
            </div>

            {/* Output Size Selection */}
            <div className="form-group">
              <label className="label">Output Dimensions</label>
              <div className="select">
                <select
                  value={JSON.stringify(size)}
                  onChange={handleSizeChange}
                  className="input"
                >
                  {SIZE_OPTIONS.map(opt => (
                    <option key={opt.label} value={JSON.stringify(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom Size Inputs */}
              {size === 'custom' && (
                <div className="mt-4 p-6 bg-slate-50/80 rounded-xl border border-slate-200">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Width (px)</label>
                      <input
                        type="number"
                        className="input"
                        placeholder="Width"
                        value={customSize.width}
                        onChange={e => {
                          const newWidth = Number(e.target.value);
                          if (model === 'amazon.titan-image-generator-v1') {
                            setCustomSize({ width: newWidth, height: newWidth });
                          } else {
                            setCustomSize({ ...customSize, width: newWidth });
                          }
                        }}
                        min={64}
                        max={maxSize}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">Height (px)</label>
                      <input
                        type="number"
                        className="input"
                        placeholder="Height"
                        value={customSize.height}
                        onChange={e => {
                          const newHeight = Number(e.target.value);
                          if (model === 'amazon.titan-image-generator-v1') {
                            setCustomSize({ width: newHeight, height: newHeight });
                          } else {
                            setCustomSize({ ...customSize, height: newHeight });
                          }
                        }}
                        min={64}
                        max={maxSize}
                        required
                        disabled={model === 'amazon.titan-image-generator-v1'}
                      />
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 bg-white/60 p-3 rounded-lg mt-4">
                    <strong>Model Limits:</strong> {model === 'amazon.titan-image-generator-v1' 
                      ? 'Amazon Titan supports up to 1408 × 1408 px (square only)' 
                      : 'Stable Diffusion SDXL supports up to 1024 × 1024 px (max 1,048,576 total pixels)'
                    }
                  </div>
                  {model === 'stability.stable-diffusion-xl-v1' && (
                    <div className="text-xs text-slate-600 mt-2">
                      Total pixels: {customSize.width * customSize.height} / 1,048,576
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Prompt Input */}
            <div className="form-group">
              <label className="label flex items-center gap-2">
                Creative Brief
                <button
                  type="button"
                  onClick={handleMicClick}
                  className={`ml-2 bg-cyan-600 text-white rounded-full p-2 shadow hover:bg-cyan-700 focus:outline-none flex items-center gap-1 ${listening ? 'animate-pulse' : ''}`}
                  aria-label="Voice to Text"
                  disabled={loading || listening}
                >
                  {listening ? (
                    <span role="img" aria-label="Listening">🎤...</span>
                  ) : (
                    <span role="img" aria-label="Microphone">🎤</span>
                  )}
                  <span className="text-xs font-medium">Voice to Text</span>
                </button>
                {/* Removed AI Suggest button - functionality temporarily disabled */}
              </label>
              <textarea
                className="w-full p-2 border rounded min-h-[96px]"
                placeholder="Enter your prompt..."
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                disabled={loading}
                rows={5}
              />
              {/* Removed Aryaa suggestion display - functionality temporarily disabled */}
            </div>

            {/* Generate Button */}
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full py-4 text-lg"
            >
              {loading ? (
                <div className="flex items-center gap-3">
                  <div className="loading-spinner"></div>
                  Generating Professional Assets...
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span>⚡</span>
                  Generate Professional Images
                </div>
              )}
            </button>
          </form>
        </div>

        {/* Suggestions Panel */}
        <div className="form-section">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <span>📈</span>
            1-click Professional Prompts
          </h3>
          <p className="text-slate-600 mb-6">
            Proven templates for business imagery
          </p>
          <div className="space-y-3 mb-10">
            {[
              "Professional product photography with modern lighting",
              "Corporate headshots with clean white background",
              "Elegant marketing materials with minimalist design",
              "Brand-focused social media content with premium aesthetics"
            ].map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handlePromptSuggestion(suggestion)}
                className="w-full text-left p-4 rounded-xl bg-slate-50/80 hover:bg-cyan-50 border border-slate-200 hover:border-cyan-300 transition-all duration-200 text-slate-700 hover:text-slate-800 text-sm leading-relaxed shadow-sm hover:shadow-md"
              >
                {suggestion}
              </button>
            ))}
          </div>
          {/* Trending Images Generator Section (now inside the same sidebar) */}
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
            <span>🔥</span>
            Trending Images Generator
          </h3>
          <p className="text-slate-600 mb-6">
            Instantly generate images from trending prompts. These are popular or timely ideas that showcase the power of LightningGen for creative and business use cases.
          </p>
          <div className="space-y-3">
            {[
              "AI-generated futuristic cityscape at sunset",
              "Minimalist workspace with vibrant colors",
              "Eco-friendly product ad with nature background",
              "Diverse team collaborating in a modern office",
              "Trending fashion shoot with bold lighting"
            ].map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handlePromptSuggestion(suggestion)}
                className="w-full text-left p-4 rounded-xl bg-yellow-50/80 hover:bg-orange-50 border border-yellow-200 hover:border-orange-300 transition-all duration-200 text-slate-700 hover:text-slate-800 text-sm leading-relaxed shadow-sm hover:shadow-md"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-600 font-medium">Error: {error}</div>
        </div>
      )}

      {/* Results Display */}
      {result && (
        <div className="mt-8">
          <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">Your Professional Assets</h3>
          <div className="card p-6">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <div className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-2">Generation Details</div>
                <div className="space-y-2 text-sm">
                  <div><strong>Prompt:</strong> {result.prompt}</div>
                  <div><strong>Brand:</strong> {result.brand}</div>
                  <div><strong>Model:</strong> {result.model}</div>
                  <div><strong>Size:</strong> {result.size && `${result.size.width}x${result.size.height}`}</div>
                  <div><strong>Latency:</strong> {(result.latency / 1000).toFixed(2)} seconds</div>
                </div>
                <div className="mt-4 p-2 bg-slate-100 rounded text-slate-700 text-sm">
                  <strong>Alt Text:</strong> {result.prompt}
                </div>
              </div>
              <div className="image-card" style={{width: '100%', height: '400px', border: '1px solid #eee', borderRadius: '8px', background: '#f8fafc', overflow: 'hidden'}}>
                <iframe
                  src={result.imageUrl}
                  title="Generated"
                  style={{width: '100%', height: '100%', border: 'none'}}
                  scrolling="auto"
                />
              </div>
            </div>
            <div className="flex justify-center gap-4">
              <a 
                href={result.imageUrl} 
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                <span>🖼️</span>
                Preview Full Screen
              </a>
              {/*
              <button
                className="btn btn-primary"
                onClick={async () => {
                  await navigator.clipboard.writeText(result.imageUrl);
                  setCopySuccess('Image URL copied! Paste in Figma plugin.');
                  setTimeout(() => setCopySuccess(null), 2000);
                }}
              >
                <span>🔗</span> Copy Image URL for Figma
              </button>
              */}
            </div>
            {copySuccess && (
              <div className="text-green-700 text-center mt-2 text-sm font-medium">{copySuccess}</div>
            )}
            {/* Removed Aryaa AI Agent suggestion section - functionality temporarily disabled */}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="footer">
        <p>Copyright &copy; 2025 LightningGen - Professional AI Image Generation</p>
      </footer>
    </div>
  );
}

export default PromptForm; 